import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";

import { useResourceContext } from "react-admin";

import { useCsvResourceImportState } from "./useCsvResourceImportState";
import { getTextByLabelForResource } from "../helpers/getTextByLabelForResource";
import { FileUploadStep } from "../shared/FileUploadStep";
import { ImportStatusStep } from "../shared/ImportStatusStep";
import { ResourceReviewStep } from "../shared/ResourceReviewStep";
import { ResourcesImportButtons } from "../shared/ResourcesImportButtons";
import * as S from "../shared/styled";

export interface CsvResourceImportDialogProps<TResource, TCreateEntity> {
  isOpen: boolean;
  handleClose: () => void;
  handleAssertInput: (input: unknown) => asserts input is TResource[];
  handleMapResourceToCreateEntity: (data: TResource[]) => TCreateEntity[];
  template?: string[][];
}

export const CsvResourceImportDialog = <TResource, TCreateEntity>({
  isOpen,
  template,
  handleClose,
  handleAssertInput,
  handleMapResourceToCreateEntity
}: CsvResourceImportDialogProps<TResource, TCreateEntity>) => {
  const resource = useResourceContext();

  const { onClose, dialogState, onFileAdded, onFileConfirm, onSave } = useCsvResourceImportState(
    handleClose,
    handleAssertInput,
    handleMapResourceToCreateEntity
  );

  const activeStep = dialogState.step;

  const generateCsvTemplate = () => {
    if (!template) {
      return;
    }

    const csvTemplate = template.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvTemplate], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "template.csv";
    a.click();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} sx={{ minWidth: "300" }}>
      <Box sx={{ p: 2 }}>
        <DialogTitle>{getTextByLabelForResource(resource, "title")}</DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel aria-label="Import steps">
            <Step>
              <StepLabel>Upload file</StepLabel>
            </Step>
            <Step>
              <StepLabel>{getTextByLabelForResource(resource, "review")}</StepLabel>
            </Step>
            <Step>
              <StepLabel>Save</StepLabel>
            </Step>
          </Stepper>

          {activeStep === 0 && (
            <>
              <FileUploadStep
                file={dialogState.file}
                accept={{
                  "text/csv": []
                }}
                message={dialogState.message}
                onFileAdded={onFileAdded}
              />
              {template && (
                <Link
                  sx={{
                    cursor: "pointer"
                  }}
                  onClick={generateCsvTemplate}
                >
                  Download template
                </Link>
              )}
            </>
          )}

          {activeStep === 1 && (
            <ResourceReviewStep importedResources={dialogState.importedResources} />
          )}

          {activeStep === 2 && <ImportStatusStep status={dialogState.status} />}
        </DialogContent>

        {activeStep === -1 && (
          <S.DialogContentContainer data-testid="incorrectCsvMessage">
            Provided CSV file is invalid.
          </S.DialogContentContainer>
        )}

        <ResourcesImportButtons
          dialogState={dialogState}
          onClose={onClose}
          onFileConfirm={onFileConfirm}
          onSave={onSave}
        />
      </Box>
    </Dialog>
  );
};
